import React, { useState } from 'react';
import { formatCurrency } from '@atc/string-fns';
import { createChainedFunction } from '@atc/create-chained-function';
import { useSelector } from 'react-redux';
import { ratesLabels } from 'axl-config';
import { Link, LinkIcon, Text } from 'reaxl';
import { Button, Link as LinkButton } from '@iris/react-components';

import { sendClick } from 'reaxl-analytics';
import { editThisOfferClick, seeDetailsDealClick, textLinkDealClick } from 'reaxl-analytics-handlers';
import { EmailCTA } from 'reaxl-email';
import clsx from 'clsx';

import DigitalRetailOverlayCTA from '../DigitalRetailOverlayCTA';
import EsntialBuyNowCTA from '../EsntialBuyNowCTA';

const pipe = <span className="padding-horizontal-2">|</span>;

const renderDeepLink = ({ creditApp, onSDPError, sdpLinkText, sdpRoute, vehicle, ...rest } = {}) => {
    const { creditAppSubmittedFlag } = creditApp;

    const route = creditAppSubmittedFlag ? 'dashboard' : sdpRoute;

    return (
        <DigitalRetailOverlayCTA
            buttonText={sdpLinkText}
            listedPrice={vehicle.sellingPrice}
            listing={vehicle}
            onError={onSDPError}
            uiContext="dealPanel"
            route={route}
            {...rest}
        />
    );
};

const renderDetails = (items = [], showEditButton, vdpUrl, onCTAClick = () => {}, isCompactView = false) => (
    <>
        {items.map(
            ({ title, value, shouldRender = true }) =>
                shouldRender && (
                    <div
                        key={title}
                        className={clsx('display-flex justify-content-between', {
                            'padding-vertical-1': !isCompactView,
                            'margin-bottom-4': isCompactView,
                        })}
                    >
                        <Text weight="bold">{title}</Text>
                        <div
                            className="text-right"
                            data-cmp={title}
                        >
                            <Text>{value()}</Text>
                        </div>
                    </div>
                )
        )}

        {showEditButton && (
            <Button
                variant="tertiary"
                className="iris:w-full iris:my-4"
                href={vdpUrl}
                onClick={onCTAClick}
            >
                Edit This Offer
            </Button>
        )}
    </>
);

const getItems = ({
    creditApp,
    dealId,
    offerDetails,
    onSDPError,
    paymentsDuck,
    tradeIn,
    vehicle,
    isAccelerate,
    vdpUrl,
    onCTAClick,
    isReceiptView,
    isEsntialDeal,
    ...rest
}) => {
    const {
        creditRating,
        loanTerm,
        rate,
        downPayment,
        leaseDownPayment,
        dealType,
        adjustedCapCost,
        mileageLimit,
        payment,
    } = offerDetails;

    const { negotiation } = vehicle;

    const isCashDeal = dealType?.toLowerCase() === 'cash';
    const isLeaseDeal = dealType?.toLowerCase() === 'lease';
    const downPaymentValue = isLeaseDeal ? leaseDownPayment : downPayment;
    const { creditAppSubmittedFlag } = creditApp;

    const linkProps = { creditApp, vehicle, paymentsDuck, onSDPError, isReceiptView, ...rest };

    const isDownPaymentLocked = creditAppSubmittedFlag && !downPaymentValue;
    const isTradeInLocked = creditAppSubmittedFlag && !tradeIn?.netTrade;
    const downPaymentLabel = downPaymentValue <= 0 || isDownPaymentLocked ? '' : formatCurrency(downPaymentValue);
    const tradeInLabel = isTradeInLocked ? '' : formatCurrency(tradeIn?.netTrade);

    const itemValue = ({
        label,
        disableLink,
        propsForDeepLink = {},
        nonDrLinkText = '',
        showPipe = true,
        hideNonDRLinkText,
    }) => (
        <>
            <Text>{label}</Text>
            {!disableLink && isAccelerate && (
                <>
                    {showPipe && pipe}
                    {renderDeepLink({ ...linkProps, ...propsForDeepLink, dealId })}
                </>
            )}
            {hideNonDRLinkText ? (
                '---'
            ) : (
                <>
                    {!isAccelerate && nonDrLinkText && !isReceiptView && (
                        <Link
                            href={vdpUrl}
                            onClick={(event) => onCTAClick(event, nonDrLinkText, propsForDeepLink.placement)}
                            undecorated
                        >
                            {nonDrLinkText}
                        </Link>
                    )}
                    {!isAccelerate && nonDrLinkText && isReceiptView && (
                        <LinkButton
                            variant="standalone"
                            className="iris:inline"
                            onClick={(event) => onCTAClick(event, nonDrLinkText, propsForDeepLink.placement)}
                        >
                            {nonDrLinkText}
                        </LinkButton>
                    )}
                </>
            )}
        </>
    );

    return [
        {
            title: (isEsntialDeal && 'Vehicle Price') || (!isCashDeal ? 'Offer Price' : 'Estimated Cash Payment'),
            shouldRender: vehicle.offerPrice,
            // If dealer has negotiation setting, then add the Edit/View links
            value: () =>
                itemValue({
                    label: formatCurrency(vehicle.offerPrice),
                    disableLink: negotiation,
                }),
        },
        {
            title: 'Credit Rating',
            shouldRender: !isCashDeal,
            value: () =>
                itemValue({
                    label: ratesLabels[creditRating]?.label,
                    propsForDeepLink: { placement: 'Credit Rating' },
                    disableLink: false,
                }),
        },
        {
            title: 'Loan Terms',
            shouldRender: !isCashDeal && !isLeaseDeal,
            value: () =>
                itemValue({
                    label: `${loanTerm} mo. at ${rate}% APR`,
                    propsForDeepLink: { placement: 'Loan Terms' },
                    disableLink: false,
                }),
        },
        {
            title: 'Lease Term',
            shouldRender: isLeaseDeal,
            value: () =>
                itemValue({
                    label: `${loanTerm} mo.`,
                    disableLink: false,
                }),
        },
        {
            title: isLeaseDeal ? 'Due At Signing' : 'Cash Down',
            shouldRender: !isCashDeal,
            value: () =>
                itemValue({
                    label: downPaymentValue > 0 && downPaymentLabel,
                    disableLink: false,
                    propsForDeepLink: {
                        sdpLinkText: downPaymentValue ? '' : 'Add Down Payment',
                        placement: isLeaseDeal ? 'Due At Signing' : 'Cash Down',
                    },
                    nonDrLinkText: (downPaymentValue <= 0 || !downPaymentValue) && 'Add Down Payment',
                    hideNonDRLinkText: isEsntialDeal && (downPaymentValue <= 0 || !downPaymentValue),
                    showPipe: !!downPaymentValue && downPaymentValue > 0,
                }),
        },
        {
            title: 'Miles Per Year',
            shouldRender: isLeaseDeal,
            value: () =>
                itemValue({
                    label: mileageLimit,
                    disableLink: false,
                }),
        },
        {
            title: 'Est. Adjusted Cap Cost',
            shouldRender: isLeaseDeal,
            value: () =>
                itemValue({
                    label: adjustedCapCost > 0 && formatCurrency(adjustedCapCost),
                    disableLink: false,
                }),
        },
        {
            title: 'Trade-In Value',
            shouldRender: true, // Always true
            value: () =>
                itemValue({
                    label: !!tradeIn?.netTrade && tradeInLabel,
                    disableLink: false,
                    propsForDeepLink: {
                        sdpLinkText: tradeIn?.netTrade ? '' : 'Value Your Vehicle',
                        sdpRoute: 'tradeInManualEntry',
                        placement: 'Trade-In Value',
                    },
                    nonDrLinkText: !tradeIn?.netTrade && 'Value Your Vehicle',
                    hideNonDRLinkText: isEsntialDeal && !tradeIn?.netTrade,
                    showPipe: !!tradeIn?.netTrade,
                }),
        },
        {
            title: 'Est. Monthly Payment',
            shouldRender: isReceiptView && !!payment,
            value: () =>
                itemValue({
                    label: formatCurrency(Math.round(payment)),
                    disableLink: true,
                }),
        },
    ];
};

function DealCardOfferDetails({
    dealsDuck,
    paymentsDuck,
    isAccelerate,
    vdpUrl,
    isReceiptView = false,
    onClick,
    handleDealReceiptOverride,
    isEsntialDeal = false,
    onEmailCTAClick,
    isCompactView = false,
    ...rest
}) {
    const { vehicle } = rest;

    const paymentsInfo = useSelector(paymentsDuck.selectors.getDuckState);

    const { ecommWebsite, zip } = vehicle;

    const [showDetails, setShowDetails] = useState(isReceiptView);

    const fireEditDealerOfferCTAClick = (event, buttonText, placement) => {
        if (isReceiptView) {
            handleDealReceiptOverride();
            sendClick(textLinkDealClick, event, { text: buttonText, inventoryId: vehicle?.id, placement });
        } else {
            sendClick(editThisOfferClick, event, { inventoryId: vehicle.id });
        }
    };

    const onEditDealCTAClick = createChainedFunction(onClick, fireEditDealerOfferCTAClick);

    const items = getItems({
        ...rest,
        paymentsInfo,
        paymentsDuck,
        isAccelerate,
        vdpUrl,
        onCTAClick: onEditDealCTAClick,
        isReceiptView,
        isEsntialDeal,
    });

    const renderedItemsCount = items.filter((item) => item.shouldRender).length;

    if (renderedItemsCount === 1) {
        return (
            <>
                {renderDetails(
                    items,
                    !isAccelerate && !isReceiptView && !isEsntialDeal,
                    vdpUrl,
                    onEditDealCTAClick,
                    isCompactView
                )}
                {isAccelerate && <hr />}
            </>
        );
    }

    const onSeeDetailsClick = (event) => {
        setShowDetails((state) => !state);
        sendClick(seeDetailsDealClick, event, { inventoryId: vehicle.id });
    };

    return (
        <>
            {!isReceiptView && (
                <div className="display-flex justify-content-between">
                    <Text weight="bold">Offer</Text>
                    <div className="text-right">
                        {renderedItemsCount > 1 && (
                            <LinkIcon
                                onClick={onSeeDetailsClick}
                                glyph={showDetails ? 'menu-up' : 'menu-down'}
                                placement="after"
                                undecorated
                            >
                                See Details
                            </LinkIcon>
                        )}
                    </div>
                </div>
            )}

            {showDetails &&
                renderDetails(
                    items,
                    !isAccelerate && !isReceiptView && !isEsntialDeal,
                    vdpUrl,
                    onEditDealCTAClick,
                    isCompactView
                )}

            {!isReceiptView && <hr />}

            {isEsntialDeal && (
                <EsntialBuyNowCTA
                    href={ecommWebsite?.href}
                    listing={vehicle} // vehicle has a limited details of listing.
                    label="Continue Your Order"
                    analyticsData={{
                        listingId: vehicle?.id,
                        placement: isReceiptView ? 'receipt' : 'deal card', // Needs tagging update as per comp
                    }}
                    bsStyle="primary"
                    className="margin-top-3"
                    dealsDuck={dealsDuck}
                    paymentsDuck={paymentsDuck}
                    zip={zip || vehicle?.dealer?.location?.address?.zip}
                    block
                />
            )}

            {!!onEmailCTAClick && (
                <EmailCTA
                    listingId={vehicle?.id}
                    type="block"
                    labelKey="contactDealer"
                    showIcon={false}
                    className="margin-top-5"
                    onClick={onEmailCTAClick}
                    buttonStyle="primary"
                    context="availableOffers"
                />
            )}
        </>
    );
}

export default DealCardOfferDetails;
